import { useState, useCallback } from "react";

/**
 * useSelection
 * ------------
 * Custom hook to manage multi-select state for a list of items.
 *
 * @param {Array} initialData - Optional initial list of selected IDs
 * @returns {Object} - { selected, setSelected, handleSelectAll, handleItemClick }
 */
const useSelection = (initialData = []) => {
  const [selected, setSelected] = useState(initialData);

  /**
   * Toggle selection of multiple items
   */
  const handleItemClick = useCallback((event, ids) => {
    const values = Array.isArray(ids) ? ids : [ids];

    setSelected((prev) => {
      let updated = [...prev];

      values.forEach((id) => {
        const exists = updated.includes(id);
        if (event.target.checked && !exists) {
          updated.push(id); // add if checked
        } else if (!event.target.checked && exists) {
          updated = updated.filter((e) => e !== id); // remove if unchecked
        }
      });

      return updated;
    });
  }, []);

  /**
   * Select or deselect all items
   * @param {boolean} checked - true = select all, false = deselect all
   * @param {Array} dataIds - array of all possible IDs
   */
  const handleSelectAll = useCallback((checked, dataIds) => {
    setSelected(() => (checked ? [...dataIds] : []));
  }, []);

  return { selected, setSelected, handleItemClick, handleSelectAll };
};

export default useSelection;
